import {
  getRecommendationDetails,
  dropManualActivities,
  dropSolution,
  setSolution,
  selectTheme,
  dropFavorites,
  getAllSolutionFilters,
  loadRecommendationDetails,
  getDefaultImages,
  getAmexSolution,
  getSolutionContexts,
  getTripId,
  getSessionId,
  getOrigin,
  getDestination,
  getAllSelectedFilters,
  getTravelersInformation,
  getPriceBudget,
} from '@/store/state/helpers';
import {computed, ref} from '@vue/composition-api';
import {
  UserActivityCategoryEnum,
  Activity,
  getReadableDuration,
  CandidatePoiItem,
  getSolutionDatesRange,
  CandidateTransportationItem,
} from '@mobi-components/global-solution';
import {useRouter} from '@/router';
import {RouterAliases} from '@/router/routerAliases';
import {ProblmSubmissionRoute} from '@/store/state/global.types';
import {loadSolution} from '../old-home-page/load-solution';
import {useSolutionFilters, VueWithModal} from '../solution-views/summary-filters';
import {useSolutionViews} from '../solution-views/solution-views';
import BookingDialogVue from './summary-booking/booking-modal.vue';
import {useScrollObserver} from './header-scroll-observer';
import {restoreTripFromMyTrips, restoreTripFromTripSample} from './solution-request';
import {formatPrices} from '@mobi-components/amex-bluesky-components';
import {useSaveSolutionDialog} from './useSaveSolutionDialod';
import {useParams} from '@/router/router';
import MulticityModal from '@components/old-home-page/multi-city-trip/multicity-modal.vue';
import {adjustToDestinationTimezone} from '@/store/state/fix-solution-dates';
import {POISearchOption, searchPoi} from '@/store/api/search-poi';
import {downloadDestinationTrips} from '@/store/api/download-destination-trips';
import {trackMapsView} from '@/store/api/trackBtnClick';
import {getUserId} from '@/services/auth-shared';
import {DateFormatter} from '@/services/date-helpers';

export function useSummary(root: VueWithModal, isPdf = false) {
  const top = ref<HTMLDivElement | null>(null);

  const {
    onFlightsOpen,
    flightDescr,
    stayActivity,
    solutionTimeFrameFilterValue,
    getFlightName,
    stayName,
    experincesCaption,
    price,
    solution,
    flights,
    isLoading,
    isFlightAvailable,
    isOneLocation,
    isFlightsOn,
    isStaysOn,
    selectedTripOption,
    availableTripOptions,
    activeTheme,
    availableContexts,
    onSwitchContext,
    ...restOfSolutionViews
  } = useSolutionViews(root);
  const {travelersFilter} = useSolutionFilters(root);

  const onBackClick = () => {
    const router = useRouter();
    router.push({name: RouterAliases.DISCOVERY_VIEW});
  };

  const onBookingOpen = () => {
    root.$modal.show(BookingDialogVue, {});
  };

  const numberOfAlternativeAccommodations = computed(() => {
    const accommodationActivities = getRecommendationDetails() || {};
    const all = (accommodationActivities[UserActivityCategoryEnum.ACCOMMODATION] || [])
      .map((x) => x?.pois || [])
      .flat();
    return all.length;
  });

  const availableExperiences = computed(() => {
    const accommodationActivities = getRecommendationDetails() || {};
    const params = useParams();
    const context = params.value.contextName;
    const pois = (accommodationActivities[UserActivityCategoryEnum.EXPERIENCE] || [])
      .map((x) => x?.pois || [])
      .flat()
      .filter((x) => x.contexts?.includes(context));
    return pois;
  });

  const experincesImg = computed(() => {
    const {expImg} = getDefaultImages();
    const images = availableExperiences.value.slice(0, 2).map((x) => x.images.medium);
    return images.length ? images : [expImg];
  });
  const experincesCaptionDerived = computed(() => {
    return isLoading.value
      ? ''
      : experincesCaption.value ||
          (selectedTripOption.value?.activities.length
            ? `<b>${selectedTripOption.value?.activities.length}</b> Places`
            : '');
  });
  const userSelectedExperincesCaptionDerived = computed(() => {
    const userSelectedActivities = (solution.value?.activities || [])
      .filter(
        (x) => x.category === UserActivityCategoryEnum.EXPERIENCE || x.category === UserActivityCategoryEnum.DINING,
      )
      .filter((x) => x.spec && !x.spec.curated);
    return isLoading.value ? '' : userSelectedActivities.length ? `<b>${userSelectedActivities.length}</b> Places` : '';
  });

  const getActivityImg = (activity?: Activity) => {
    const {stayImg, flightImg} = getDefaultImages();
    const defaultImg = activity?.category === UserActivityCategoryEnum.ACCOMMODATION ? stayImg : flightImg;
    return activity?.images.small || activity?.images.medium || defaultImg;
  };

  const stayImg = computed(() => {
    const {stayImg} = getDefaultImages();
    if (!isStaysOn.value || !stayActivity.value) {
      return stayImg;
    }
    return getActivityImg(stayActivity.value);
  });
  const stayNameDerived = computed(() => {
    if (!isStaysOn.value) {
      return '';
    }

    const acc =
      (selectedTripOption.value?.accommodations.length && selectedTripOption.value?.accommodations[0]) || null;
    return stayName.value || acc?.name;
  });

  const stayPerks = computed(() => {
    return (stayActivity.value?.explanations || []).join(', ');
  });

  const getActivityAddress = (activity?: Activity) => {
    if (!activity) {
      return '';
    }
    return (activity.source?.content as CandidatePoiItem)?.location?.address || '';
  };

  const getActivityRating = (activity?: Activity) => {
    return activity?.rating || 0;
  };

  const stayAddress = computed(() => {
    return getActivityAddress(stayActivity.value);
  });
  const stayRating = computed(() => {
    return getActivityRating(stayActivity.value);
  });

  const solutionFilters = computed(() => {
    return getAllSolutionFilters();
  });

  const stayTags = computed(() => {
    const all = solutionFilters.value
      .filter((x) => x.domain === UserActivityCategoryEnum.ACCOMMODATION && x.value)
      .map((x) => x.name);
    const rest = all.length - 3;
    return rest > 0 ? [...all.slice(0, 3), `+${rest} More`] : all.slice(0, 3);
  });

  const exploreTags = computed(() => {
    const all = solutionFilters.value
      .filter((x) => x.domain === UserActivityCategoryEnum.EXPERIENCE && x.value)
      .map((x) => x.name);
    const rest = all.length - 3;
    return rest > 0 ? [...all.slice(0, 3), `+${rest} More`] : all.slice(0, 3);
  });

  const flightName = computed(() => {
    if (isLoading.value) {
      return '';
    }
    return isFlightAvailable.value && flightDescr.value
      ? `Flight: ${flightDescr.value}`
      : 'Sorry, <br /> we can’t find any flights';
  });

  function getRecord<T>(values: T[]): Record<number, T> {
    return values.reduce((h: Record<number, T>, x, i) => {
      h[i] = x;
      return h;
    }, {});
  }

  const dates = computed(() => {
    return getRecord(flights.value.map(DateFormatter.getFlightDates));
  });

  const departure = computed(() => {
    // @ts-ignore
    if (!dates.value[0]) return '';
    // @ts-ignore
    return `<b>Departure</b> ${dates.value[0].departure}-${dates.value[0].arrival} <br /> <b>Flight</b> ${dates.value[0].name}`;
  });
  const arrival = computed(() => {
    // @ts-ignore
    if (!dates.value[1]) return '';
    // @ts-ignore
    return `<b>Arrival</b> ${dates.value[1].departure}-${dates.value[1].arrival} <br /> <b>Flight</b> ${dates.value[1].name}`;
  });

  const airlines = computed(() => {
    let selectedFlights: Readonly<Array<{types: string[]}>> = [];
    if (!selectedTripOption.value || flights.value.length > 0) {
      selectedFlights = flights.value || [];
    } else {
      selectedFlights = selectedTripOption.value.flights || [];
    }
    return selectedFlights
      .map((x) => (x.types || []).slice(0, 2))
      .flat()
      .join(', ');
  });

  const isSummaryMapActiveState = ref(false);

  const isSummaryMapActive = computed(() => {
    return isSummaryMapActiveState.value;
  });

  const onToggleSummaryMap = () => {
    if (!isSummaryMapActiveState.value) {
      const userId = getUserId();
      const problemId = solution.value?.problem_id;
      trackMapsView(userId, problemId);
    }
    isSummaryMapActiveState.value = !isSummaryMapActiveState.value;
  };

  const onShowMap = () => {
    isSummaryMapActiveState.value = true;
    root.$nextTick(() => {
      if (top.value) {
        top.value.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    });
  };

  const solutionDates = computed(() => {
    if (!selectedTripOption.value) {
      return solutionTimeFrameFilterValue.value;
    }
    const start = new Date(selectedTripOption.value.scheduled_start_time * 1000);
    const end = new Date(selectedTripOption.value.scheduled_end_time * 1000);
    return solutionTimeFrameFilterValue.value ? solutionTimeFrameFilterValue.value : getReadableDuration(start, end);
  });

  const isTripInThePast = computed(() => {
    const {startDate} = getSolutionDatesRange(solution.value) || {startDate: new Date()};
    return startDate.valueOf() < new Date().valueOf();
  });

  const flightDirivedName = computed(() => {
    if (!selectedTripOption.value) {
      return isFlightAvailable.value ? '' : flightName.value;
    }

    const transportationItems = (flights.value.map((x) => x.source?.content) || []) as CandidateTransportationItem[];

    return isFlightAvailable.value ? `Flight: ${getFlightName(transportationItems)}` : flightName.value;
  });

  const flightDescription = computed(() => {
    if (!isFlightsOn.value) {
      return 'Turned Off';
    }
    if (isOneLocation.value) {
      return '';
    }
    return isFlightAvailable.value && flightDescr.value ? flightName.value : flightDirivedName.value;
  });

  const tripOptions = computed(() => {
    const availableTrips = availableTripOptions.value
      .filter((x) => x.theme !== '')
      .filter((x) => x.theme === activeTheme.value)
      .map((x) => ({id: x.trip_id, theme: x.theme}));
    return activeTheme.value === 'Custom' ? [{id: 'Custom', theme: activeTheme.value}] : availableTrips;
  });

  const onSelectTheme = async (id: string) => {
    if (id === 'Custom') {
      return;
    }
    const trip = availableTripOptions.value.find((x) => x.trip_id === id);
    if (trip) {
      const oldTheme = activeTheme.value;
      selectTheme(trip.theme);
      const contexts = getSolutionContexts();
      const oldSolution = await dropSolution();

      await dropManualActivities();

      const availableFlights = trip?.flights || [];
      const accommodations = (trip?.accommodations || []).map((x) => ({id: x.id, name: x.name}));

      const result = await loadSolution(
        trip?.trip_id,
        (trip?.activities || []).concat(accommodations),
        ProblmSubmissionRoute.SOLUTION,
        availableFlights,
        contexts,
      );
      if (!result) {
        setSolution(oldSolution);
        selectTheme(oldTheme);
      }
    }
  };

  const isStartFromScratchVisible = computed(() => {
    return activeTheme.value !== 'Custom';
  });

  const startFromScratch = async () => {
    const trip = availableTripOptions.value.find((x) => !x.pois || x.pois.length === 0);
    if (trip) {
      const oldTheme = activeTheme.value;
      selectTheme('Custom');
      const oldSolution = await dropSolution();

      await dropManualActivities();
      await dropFavorites();
      const result = await loadSolution(trip?.trip_id, [], ProblmSubmissionRoute.SOLUTION, []);
      if (!result) {
        setSolution(oldSolution);
        selectTheme(oldTheme);
      }
    }
  };

  const numberOfTravelers = computed(() => {
    const {adults, children} = travelersFilter;
    return adults + children;
  });

  restoreTripFromMyTrips(isPdf)
    .then(() => {
      const solution = getAmexSolution();
      if (!solution) {
        restoreTripFromTripSample(isPdf);
      }
    })
    .then(() => {
      const params = useParams();
      return loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, params.value.contextName);
    });

  const solutionPrice = computed(() => {
    // @ts-ignore
    return formatPrices(price.value);
  });

  const onDatesSelectedValidate = (newContext: CandidatePoiItem, contextsWithDates?: Record<string, Date>) => {
    if (!contextsWithDates) {
      return;
    }

    const contexts = getSolutionContexts();

    const allContexts = [...contexts, newContext].map((x) => {
      const ms = adjustToDestinationTimezone(contextsWithDates[x.name].valueOf(), x.location.time_zone);
      const noon = Math.round((ms + 12 * 3600 * 1000) / 1000);
      return {
        ...x,
        end_timestamp: noon,
      };
    });

    const tripId = getTripId();
    const origin = getOrigin();
    const dest = getDestination();
    const filters = getAllSelectedFilters();
    const budget = getPriceBudget(ProblmSubmissionRoute.SOLUTION);
    const travelersInformation = getTravelersInformation();
    downloadDestinationTrips(
      tripId,
      getUserId(),
      origin,
      dest,
      filters,
      budget,
      travelersInformation,
      allContexts,
      true,
    );
  };

  const onAddDestination = (newContext: CandidatePoiItem) => {
    const tripDescription = {
      id: newContext.name,
      name: newContext.name.replace('_', ', '),
      img: newContext.images.small,
      dateRange: null,
      isActive: false,
    };

    const allTrips = [
      ...availableContexts.value.map((x) => ({
        ...x,
        dateRange: {
          startDate: new Date(x.dateRange.startDate * 1000),
          endDate: new Date(x.dateRange.endDate * 1000),
        },
      })),
      tripDescription,
    ];

    const onDatesReady = (contextsWithDates?: Record<string, Date>) => {
      onDatesSelectedValidate(newContext, contextsWithDates);
    };

    root.$modal
      .show(MulticityModal, {availableTrips: allTrips, img: newContext.images.small, onDatesReady})
      // @ts-ignore
      .then(async (contextsWithDates?: Record<string, Date>) => {
        if (!contextsWithDates) {
          return;
        }

        const contexts = getSolutionContexts();

        const allContexts = [...contexts, newContext].map((x) => {
          const ms = adjustToDestinationTimezone(contextsWithDates[x.name].valueOf(), x.location.time_zone);
          const noon = Math.round((ms + 12 * 3600 * 1000) / 1000);
          return {
            ...x,
            end_timestamp: noon,
          };
        });

        const tripId = getTripId();
        const oldSolution = await dropSolution();
        await dropManualActivities();
        const result = await loadSolution(tripId, [], ProblmSubmissionRoute.SOLUTION, [], allContexts);
        if (!result) {
          setSolution(oldSolution);
        }
        // return updateSolution(tripId, ProblmSubmissionRoute.SOLUTION, [], LunchTime.ANY, allContexts);
      })
      .then(() => {
        const sessionId = getSessionId();
        const router = useRouter();
        const params = useParams();
        router.push({
          name: RouterAliases.SUMMARY_VIEW,
          params: {
            ...params.value,
            tripId: sessionId,
          },
        });
      })
      .catch(() => {
        // modal closed
      });
  };

  const destValue = ref('');
  const fromOptions = ref<POISearchOption[]>([]);
  const isFromOptionsLoading = ref(false);

  const onDestValueChange = async (value: string) => {
    destValue.value = value;
    isFromOptionsLoading.value = true;
    const options = await searchPoi(value, '', true, true);
    fromOptions.value = options;
    isFromOptionsLoading.value = false;
  };

  const clearInput = () => {
    fromOptions.value = [];
    destValue.value = '';
  };

  const onDestinationSelect = (value: CandidatePoiItem) => {
    clearInput();
    onAddDestination(value);
  };

  return {
    onDestValueChange,
    onDestinationSelect,
    clearInput,
    destValue,
    fromOptions,
    isFromOptionsLoading,
    onBackClick,
    flightName,
    stayImg,
    stayPerks,
    numberOfAlternativeAccommodations,
    experincesImg,
    solution,
    ...restOfSolutionViews,
    flights,
    stayTags,
    exploreTags,
    diningCtegory: UserActivityCategoryEnum.DINING,
    exploreCategory: UserActivityCategoryEnum.EXPERIENCE,
    stayCategory: UserActivityCategoryEnum.ACCOMMODATION,
    isSummaryMapActive,
    onToggleSummaryMap,
    activeTheme,
    tripOptions,
    onSelectTheme,
    isStartFromScratchVisible,
    startFromScratch,
    onShowMap,
    top,
    departure,
    arrival,
    airlines,
    onFlightsOpen,
    onBookingOpen,
    numberOfTravelers,
    ...useScrollObserver(),
    solutionDates,
    flightDescription,
    isOneLocation,
    stayNameDerived,
    experincesCaptionDerived,
    userSelectedExperincesCaptionDerived,
    solutionPrice,
    stayAddress,
    getActivityAddress,
    getActivityRating,
    getActivityImg,
    stayRating,
    price,
    ...useSaveSolutionDialog(root),
    isFooterAvailable: process.env.VUE_APP_IS_FOOTER_VISIBLE || false,
    isLoading,
    isFlightAvailable,
    isFlightsOn,
    isStaysOn,
    isTripInThePast,
    availableContexts,
    onAddDestination,
    onSwitchContext,
  };
}
